import {useEffect, useRef} from 'react'
import {getGenericContentLink, type GenericContent} from '../../../../../../brand/lib/types/contentful'
import {animateAssetZoom} from '../../_utils/animations'
import {animateParallax, type AnimationParallaxElement} from '../../_utils/parallax'
import {t} from '@github-ui/swp-core/lib/utils/localization'

import {YouTubePlayer} from '../../_shared/YouTubePlayer/YouTubePlayer'
import {MediaAsset} from '../../_shared/MediaAsset/MediaAsset'

import Styles from './Media.module.css'

type Props = {
  content: GenericContent
  location: string
  sectionIndex: number
  loading?: 'eager' | 'lazy'
}

export function Media(props: Props) {
  const {content, location, loading, sectionIndex} = props

  const {media} = content.fields
  const link = getGenericContentLink(content)

  // Animation
  const wrapperRef = useRef<HTMLDivElement>(null)
  const assetRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // Don't use parallax animation for first section
    if (sectionIndex === 0) {
      const elements: Element[] = []
      if (assetRef.current) elements.push(assetRef.current)

      if (wrapperRef.current) {
        return animateAssetZoom(elements, wrapperRef.current, true)
      }
    } else {
      const elements: AnimationParallaxElement[] = []
      if (assetRef.current) elements.push({element: assetRef.current})

      if (wrapperRef.current) {
        return animateParallax(elements, wrapperRef.current)
      }
    }
  }, [sectionIndex])

  return (
    <div className={Styles.Media} ref={wrapperRef}>
      {link ? (
        <YouTubePlayer
          youtubeUrl={link.fields.href}
          heading={link.fields.text || t('Video')}
          location={location}
          poster={media?.at(0)} // Use first media asset as poster image
          loading={loading}
        />
      ) : (
        media && (
          <div ref={assetRef} data-animate="container">
            <div data-animate="item">
              <MediaAsset content={media} location={location} loading={loading} />
            </div>
          </div>
        )
      )}
    </div>
  )
}
